import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Popper,
  ClickAwayListener,
  Grid2,
  Paper,
  Autocomplete,
  Stack,
  TextField,
  Divider,
  Chip,
} from '@mui/material';
import { ContentQueryParams, SubCategoryType } from '../../data/types/content.interface';
import { useLocation } from 'react-router-dom';
import { CloseIcon, DownIcon, UpIcon } from '../shared/components/AppIcons';
import { TagType } from '../../data/types/tags.interface';
import { capitalize, isEmpty } from 'lodash';

const useStyles = makeStyles({
  contentSection: {
    padding: '17px 17px 14px 17px',
    width: '514px',
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  save: {
    width: '127px',
    height: '40px',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  textField: {
    '& .MuiInputBase-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      padding: '4px 8px',
      backgroundColor: '#fff',
      height: 'auto',
      marginBottom: '4px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 0',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '0 !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
    '& Chip': {
      border: '1px solid ##E1E3E8',
      color: '#5F6774',
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

type Props = {
  open: boolean;
  tagType: string;
  list?: TagType[];
  contentTypes?: TagType[];
  anchorEl: null | HTMLElement;
  categories?: SubCategoryType[];
  subCategories?: SubCategoryType[];
  themeList?: TagType[];
  handleClose: () => void;
  handleFilters: (filters: ContentQueryParams) => void;
};

const FilterModal: React.FC<Props> = ({
  open,
  anchorEl,
  tagType,
  list,
  contentTypes,
  categories,
  subCategories,
  themeList,
  handleClose,
  handleFilters,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const [selectedTags, setSelectedTags] = useState<TagType[]>([]);
  const [selectedType, setSelectedType] = useState<TagType[]>([{ id: 3, name: 'video' }]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [openDropDown, setOpenDropDown] = useState(false);

  const defaultProps = {
    options: contentTypes || [],
    getOptionLabel: (option: TagType) => option.name,
  };
  const defaultTagProps = {
    options: list || [],
    getOptionLabel: (option: TagType) => option.name,
  };
  const defaultCategoryProps = {
    options: categories || [],
    getOptionLabel: (option: SubCategoryType) => option.name,
  };
  const defaultSubCategoryProps = {
    options: subCategories || [],
    getOptionLabel: (option: SubCategoryType) => option.name,
  };
  const defaultThemeProps = {
    options: themeList || [],
    getOptionLabel: (option: TagType) => option.name,
  };

  const handleCategoryChange = (e: React.SyntheticEvent<Element, Event>, values: SubCategoryType[]) => {
    setSelectedCategory(values);
  };
  const handleSubCategoryChange = (e: React.SyntheticEvent<Element, Event>, values: SubCategoryType[]) => {
    setSelectedSubCategory(values);
  };
  const handleTagChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedTags(values);
  };
  const handleAssetTypeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedType(values);
  };
  const handleThemeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedThemes(values);
  };

  useEffect(() => {
    setSelectedTags([]);
    setSelectedType([]);
    setSelectedThemes([]);
    setSelectedCategory([]);
    setSelectedSubCategory([]);
  }, [location.pathname]);

  const handleApply = () => {
    const props: ContentQueryParams = {
      perPage: 50,
      page: 1,
      assetType: selectedType,
      tags: selectedTags,
      themes: selectedThemes,
      category: selectedCategory,
      subCategory: selectedSubCategory,
    };
    if (!tagType) {
      delete props.tags;
    }
    handleFilters(props);
  };

  const handleCancel = () => {
    setSelectedTags([]);
    setSelectedCategory([]);
    setSelectedThemes([]);
    setSelectedSubCategory([]);
    setSelectedType([]);
    handleClose();
  };

  return (
    <Popper
      open={open}
      aria-modal="true"
      role="dialog"
      anchorEl={anchorEl}
      placement="bottom-end"
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper variant="elevation" className={classes.contentSection}>
          <Grid2 container direction="column">
            <Grid2 container fontWeight="bold">
              <span>Filter By</span>
            </Grid2>

            <Grid2 mt={2}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            <Grid2 container direction="row" mt={2} minHeight={76} flexWrap="nowrap">
              <Grid2 container direction="column" sx={{ width: '49%' }}>
                <label>Category</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultCategoryProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            deleteIcon={<CloseIcon viewBox="5 5 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>

              <Grid2 container direction="column" ml={1} sx={{ width: '49%' }}>
                <label>Sub Category</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultSubCategoryProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedSubCategory}
                    onChange={handleSubCategoryChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            deleteIcon={<CloseIcon viewBox="5 5 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>
            </Grid2>

            {tagType && (
              <Grid2 container direction="row" minHeight={76} flexWrap="nowrap">
                <Grid2 container direction="column" sx={{ width: '49%' }}>
                  <label>Content Types</label>
                  <Stack spacing={1} className={classes.textField}>
                    <Autocomplete
                      {...defaultProps}
                      id="auto-highlight"
                      autoHighlight
                      multiple
                      value={selectedType}
                      onChange={handleAssetTypeChange}
                      renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                      onOpen={() => setOpenDropDown(true)}
                      onClose={() => setOpenDropDown(false)}
                      popupIcon={
                        openDropDown ? (
                          <UpIcon className={classes.dropdownIcon} />
                        ) : (
                          <DownIcon className={classes.dropdownIcon} />
                        )
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option.name}
                              size="small"
                              deleteIcon={<CloseIcon viewBox="5 5 27 27" sx={{ width: '22px', height: '22px' }} />}
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Stack>
                </Grid2>
                <Grid2 container direction="column" ml={1} sx={{ width: '49%' }}>
                  <label>{capitalize(tagType)}</label>
                  <Stack spacing={1} className={classes.textField}>
                    <Autocomplete
                      {...defaultTagProps}
                      id="auto-highlight"
                      autoHighlight
                      multiple
                      value={selectedTags}
                      onChange={handleTagChange}
                      renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                      onOpen={() => setOpenDropDown(true)}
                      onClose={() => setOpenDropDown(false)}
                      popupIcon={
                        openDropDown ? (
                          <UpIcon className={classes.dropdownIcon} />
                        ) : (
                          <DownIcon className={classes.dropdownIcon} />
                        )
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option.name}
                              size="small"
                              deleteIcon={<CloseIcon viewBox="5 5 27 27" sx={{ width: '22px', height: '22px' }} />}
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Stack>
                </Grid2>
              </Grid2>
            )}

            {isEmpty(tagType) && (
              <Grid2 container direction="column" sx={{ width: '100%' }}>
                <label>Theme</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultThemeProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedThemes}
                    onChange={handleThemeChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                            deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>
            )}

            <Grid2 mt={2}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            <Grid2 container mt={2} justifyContent="end">
              <Button size="small" variant="text" type="button" className={classes.cancel} onClick={handleCancel}>
                cancel
              </Button>
              <Button size="small" className={classes.save} variant="contained" onClick={handleApply}>
                <span style={{ fontSize: 'h5', textTransform: 'none' }}>Apply Filters</span>
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FilterModal;
